import { useCallback, useRef, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../API';
import OpenSeadragonAnnotate from '../../widgets/OpenSeadragonAnnotate';
import NotesControl from '../../components/NotesControl';
import { ItemMetadataCapsule } from './itemMetadata/ItemMetadataCapsule';
import { getUIMetadata } from './uiRegistry';
import { findItemRowForItemName } from './itemMetadata/itemInfoUtils';
import {
    normalizeItemMetadataConfig,
    getDisplayableItemMetadataEntries,
} from './itemMetadata/itemMetadataModel';
import ViewerTopBar from './ViewerTopBar';
import styles from './ProjectViewer.module.css';
import {
    buildReviewFeatureCollections,
    colorForUser,
} from './projectReview/buildReviewFeatureCollections';
import ReviewDisplayControls from './projectReview/ReviewDisplayControls';
import {
    DEFAULT_COLORIZE_BY_ANNOTATOR,
    DEFAULT_REVIEW_FILL_OPACITY,
} from './projectReview/reviewDisplayConstants';

const TOOLKIT_OPTIONS = { toolbar: false, layerUI: false, layout: false };

function Legend({ annotations, selectedUserId, onSelectUser }) {
    if (!annotations.length) return null;
    const users = annotations.map((a) => ({
        userId: a.userId,
        label: a.displayName || a.login || a.userId,
    }));
    return (
        <div className={styles.reviewLegend}>
            <span className={styles.reviewLegendLabel}>Participants:</span>
            <button
                type="button"
                className={`${styles.reviewLegendChip} ${!selectedUserId ? styles.reviewLegendChipActive : ''}`}
                onClick={() => onSelectUser(null)}
            >
                <span className={styles.reviewLegendColor} style={{ backgroundColor: 'transparent', borderStyle: 'dashed' }} />
                All
            </button>
            {users.map((u) => {
                const active = selectedUserId === u.userId;
                return (
                    <button
                        key={u.userId}
                        type="button"
                        className={`${styles.reviewLegendChip} ${active ? styles.reviewLegendChipActive : ''}`}
                        onClick={() => onSelectUser(active ? null : u.userId)}
                    >
                        <span
                            className={styles.reviewLegendColor}
                            style={{ backgroundColor: colorForUser(u.userId) }}
                        />
                        {u.label}
                    </button>
                );
            })}
        </div>
    );
}

function ProjectReviewViewer() {
    const { folderId, itemId } = useParams();
    const navigate = useNavigate();

    const [projectDoc, setProjectDoc] = useState(null);
    const [items, setItems] = useState([]);
    const [overlayData, setOverlayData] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [toolkitReady, setToolkitReady] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tooltip, setTooltip] = useState(null);
    const [colorizeByAnnotator, setColorizeByAnnotator] = useState(DEFAULT_COLORIZE_BY_ANNOTATOR);
    const [reviewFillOpacity, setReviewFillOpacity] = useState(DEFAULT_REVIEW_FILL_OPACITY);
    const toolkitRef = useRef(null);
    const lastHoveredItemRef = useRef(null);

    // Fetch project and items only when folderId changes (for nav and item list).
    // Do not depend on itemId — same as ProjectViewer — so switching items does not
    // set loading true and flash the whole UI.
    useEffect(() => {
        if (!folderId) return;
        let cancelled = false;
        setLoading(true);
        setError(null);

        Promise.all([
            api.get(`projects/${folderId}`, {}),
            api.get(`projects/${folderId}/items`, {}),
        ]).then(([projectRes, itemsRes]) => {
            if (cancelled) return;
            if (projectRes.status === 200) {
                setProjectDoc(projectRes.data);
            } else {
                setError(projectRes.data?.message || 'Failed to load project.');
            }
            if (itemsRes.status === 200) {
                setItems(itemsRes.data?.items ?? []);
            }
        }).catch((e) => {
            if (!cancelled) setError(e.message);
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [folderId]);

    // Reset toolkit state and overlay data when item changes.
    useEffect(() => {
        setToolkitReady(false);
        toolkitRef.current = null;
        setOverlayData(null);
    }, [itemId]);

    // Fetch annotations/all only when toolkit is ready (same pattern as RulerMeasurementUI).
    useEffect(() => {
        if (!toolkitReady || !itemId) return;

        let cancelled = false;
        api.get(`projects/${folderId}/items/${itemId}/annotations/all`, {})
            .then((res) => {
                if (cancelled) return;
                if (res.status === 200) {
                    setOverlayData(res.data || null);
                }
            })
            .catch(() => {
                // Silently ignore; user can still view the slide without overlay data
            });

        return () => {
            cancelled = true;
        };
    }, [folderId, itemId, toolkitReady]);

    const allFeatureCollections = useMemo(
        () =>
            buildReviewFeatureCollections({
                overlayData,
                selectedUserId,
                colorizeByAnnotator,
                fillOpacitySlider: reviewFillOpacity,
            }),
        [overlayData, selectedUserId, colorizeByAnnotator, reviewFillOpacity],
    );

    // Load overlays when toolkit is ready and overlay data matches current item.
    // Same pattern as RulerMeasurementUI useMeasurements: gate on toolkitReady, then
    // call addFeatureCollections directly (no defer/retry).
    useEffect(() => {
        if (!toolkitReady) return;
        const tk = toolkitRef.current;
        if (!tk) return;

        const dataItemId = overlayData?.itemId != null ? String(overlayData.itemId) : null;
        const currentId = itemId != null ? String(itemId) : null;

        try {
            tk.addFeatureCollections([], true);
            if (dataItemId === currentId && allFeatureCollections.length > 0) {
                const parentImage = tk.overlay?.viewer?.world?.getItemAt?.(0);
                tk.addFeatureCollections(allFeatureCollections, true, parentImage);
            }
        } catch (err) {
            // Keep try/catch for robustness; no log unless debugging
        }
    }, [toolkitReady, itemId, overlayData?.itemId, allFeatureCollections]);

    // View-level mousemove with project hit-testing and tooltip for annotator label.
    useEffect(() => {
        if (!toolkitReady) return;
        const tk = toolkitRef.current;
        if (!tk?.paperScope?.view || !tk.paperScope?.project) return;

        const view = tk.paperScope.view;
        const project = tk.paperScope.project;

        function findReviewUserLabel(item) {
            let current = item;
            while (current) {
                const label = current.data?.userdata?.reviewUserLabel;
                if (label != null) return label;
                current = current.parent;
            }
            return null;
        }

        // RulerMeasurement (and similar) set isGeoJSONFeature only on the top-level Group; hitTest
        // often returns a descendant (e.g. path or label inside a segment group). So we accept any
        // item that has an ancestor with isGeoJSONFeature, then normalize to that ancestor.
        function hasGeoJSONFeatureInAncestry(p) {
            let current = p;
            while (current) {
                if (current.isGeoJSONFeature) return true;
                current = current.parent;
            }
            return false;
        }
        function getTopLevelFeatureItem(p) {
            let current = p;
            let top = p;
            while (current) {
                if (current.isGeoJSONFeature) top = current;
                current = current.parent;
            }
            return top;
        }

        const hitTestOptions = {
            fill: true,
            stroke: true,
            segments: true,
            tolerance: 5,
            match: (i) => hasGeoJSONFeatureInAncestry(i.item),
        };

        /*
         * Hover emphasis (opacity-dimming) intentionally disabled.
         *
         * We previously dimmed non-hovered measurements by setting Paper.js items'
         * `opacity` on hover transitions. Profiling showed this caused noticeable
         * performance issues during zoom (compositing/blending cost), even with a
         * relatively small number of measurement objects.
         *
         * If we reintroduce emphasis in the future, consider alternatives that avoid
         * large-area alpha blending (e.g. highlight-only effects, or pausing emphasis
         * while OpenSeadragon is animating/zooming).
         */
        // const HOVER_DIMMED_OPACITY = 0.4;
        // function setHoverEmphasis(hoveredItem) {
        //     const allFeatures = project.getItems({ match: (i) => i.isGeoJSONFeature });
        //     allFeatures.forEach((f) => {
        //         f.opacity = (hoveredItem == null || f === hoveredItem) ? 1 : HOVER_DIMMED_OPACITY;
        //     });
        // }
        // function clearHoverEmphasis() {
        //     const allFeatures = project.getItems({ match: (i) => i.isGeoJSONFeature });
        //     allFeatures.forEach((f) => { f.opacity = 1; });
        // }

        const handleMouseMove = (ev) => {
            const point = ev.point;
            let hitResult = project.hitTest(point, hitTestOptions);
            const rawItem = hitResult?.item || null;
            const item = rawItem && hasGeoJSONFeatureInAncestry(rawItem) ? getTopLevelFeatureItem(rawItem) : null;
            const label = item ? findReviewUserLabel(item) : null;

            if (lastHoveredItemRef.current !== item) {
                lastHoveredItemRef.current = item;
                const clientX = ev.event?.clientX ?? 0;
                const clientY = ev.event?.clientY ?? 0;
                if (label != null) {
                    setTooltip({ text: label, clientX, clientY });
                } else if (item) {
                    setTooltip({ text: '(Unknown)', clientX, clientY });
                } else {
                    setTooltip(null);
                }
            }
        };

        const handleMouseLeave = () => {
            lastHoveredItemRef.current = null;
            setTooltip(null);
        };

        view.on('mousemove', handleMouseMove);
        view.on('mouseleave', handleMouseLeave);

        return () => {
            lastHoveredItemRef.current = null;
            setTooltip(null);
            try {
                view.off('mousemove', handleMouseMove);
                view.off('mouseleave', handleMouseLeave);
            } catch (_) {
                // View/project may be destroyed (e.g. rapid navigation); ignore
            }
        };
    }, [toolkitReady, overlayData?.itemId]);

    const handleToolkitReady = useCallback((tk) => {
        toolkitRef.current = tk;
        setToolkitReady(true);
    }, []);

    const currentItem = useMemo(
        () => items.find((it) => String(it._id) === String(itemId)) || null,
        [items, itemId],
    );

    // Project-doc derivations and notes lookup. These MUST sit above the
    // loading/error/!currentItem early returns: useMemo is a hook, and React's
    // hook-order invariant breaks ("Rendered more hooks than during the previous
    // render") if loading=true short-circuits the first render but a later
    // settled render reaches the useMemo.
    const projectName = projectDoc?.name || 'Project';
    const notesConfig = projectDoc?.girderProjects?.notes ?? null;
    const selectedUserNotes = useMemo(() => {
        if (!notesConfig?.enabled) return '';
        const anns = overlayData?.annotations || [];
        if (!selectedUserId) return '';
        const row = anns.find((a) => String(a.userId) === String(selectedUserId));
        return typeof row?.notes === 'string' ? row.notes : '';
    }, [notesConfig?.enabled, overlayData?.annotations, selectedUserId]);

    const uiConfigForMetadata = projectDoc?.girderProjects?.annotationUI?.config ?? {};
    const itemMetadataNormalized = useMemo(
        () => normalizeItemMetadataConfig(uiConfigForMetadata),
        [uiConfigForMetadata],
    );
    const itemMetadataRows = itemMetadataNormalized.itemMetadata?.rows ?? [];
    const itemMetadataMatchOpts = useMemo(() => ({
        trimWhitespace: itemMetadataNormalized.itemMetadata?.trimWhitespace !== false,
        caseInsensitive: Boolean(itemMetadataNormalized.itemMetadata?.caseInsensitive),
    }), [itemMetadataNormalized]);

    function goToItem(itemDoc) {
        if (!itemDoc) return;
        navigate(`/projects/${folderId}/review/${itemDoc._id}`);
    }

    if (loading) {
        return <div className={styles.loading}>Loading review viewer…</div>;
    }

    if (error) {
        return (
            <div className={styles.centeredMessage}>
                <div className={styles.errorMessage}>{error}</div>
                <Link to={`/projects/${folderId}`} className={styles.backLink}>← Back to project</Link>
            </div>
        );
    }

    if (!currentItem) {
        return (
            <div className={styles.centeredMessage}>
                <div className={styles.errorMessage}>Item not found in this project.</div>
                <Link to={`/projects/${folderId}`} className={styles.backLink}>← Back to project</Link>
            </div>
        );
    }

    const metadataRow = currentItem?.name
        ? findItemRowForItemName(
            itemMetadataRows,
            currentItem.name,
            itemMetadataMatchOpts,
        )
        : null;
    const itemMetadataEntries = getDisplayableItemMetadataEntries(
        uiConfigForMetadata,
        metadataRow,
    );
    const reviewUiId = projectDoc?.girderProjects?.annotationUI?.id;
    const itemMetadataPlacement = getUIMetadata(reviewUiId)?.itemMetadataPlacement ?? 'right';

    return (
        <div className={styles.viewerPage}>
            <ViewerTopBar
                folderId={folderId}
                projectName={`Review: ${projectName}`}
                items={items}
                currentItem={currentItem}
                onItemChange={goToItem}
                taskControls={null}
                itemMetadata={
                    itemMetadataEntries.length > 0 ? (
                        <ItemMetadataCapsule
                            uiConfig={uiConfigForMetadata}
                            row={metadataRow}
                        />
                    ) : null
                }
                itemMetadataPlacement={itemMetadataPlacement}
                taskAccessory={
                    notesConfig?.enabled ? (
                        <NotesControl
                            mode="readOnly"
                            config={notesConfig}
                            notes={selectedUserNotes}
                            readOnly
                        />
                    ) : null
                }
                contextToolbar={(
                    <ReviewDisplayControls
                        colorizeByAnnotator={colorizeByAnnotator}
                        onColorizeByAnnotatorChange={setColorizeByAnnotator}
                        fillOpacity={reviewFillOpacity}
                        onFillOpacityChange={setReviewFillOpacity}
                    />
                )}
                progressMap={{}}
                backLinkTo={`/projects/${folderId}/progress`}
                backLinkLabel="← Progress"
                showCompleteAndReset={false}
                showItemProgressInDropdown={false}
            />
            <div className={styles.viewerBody}>
                <div className={styles.reviewSidebar}>
                    <h4 className={styles.reviewSidebarTitle}>Participants</h4>
                    <Legend
                        annotations={overlayData?.annotations || []}
                        selectedUserId={selectedUserId}
                        onSelectUser={setSelectedUserId}
                    />
                    <p className={styles.reviewSidebarHint}>
                        This view is read-only. Use the main viewer to edit annotations.
                    </p>
                </div>
                <div className={styles.reviewViewport}>
                    <OpenSeadragonAnnotate
                        key={currentItem._id}
                        itemId={currentItem._id}
                        toolkitOptions={TOOLKIT_OPTIONS}
                        style={{ width: '100%', height: '100%' }}
                        onToolkitReady={handleToolkitReady}
                    />
                    {tooltip && (
                        <div
                            className={styles.reviewTooltip}
                            style={{
                                left: tooltip.clientX,
                                top: tooltip.clientY + 12,
                            }}
                        >
                            {tooltip.text}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ProjectReviewViewer;

